import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Volume2, Pencil, Trash2, PlayCircle, Plus, ShieldCheck } from "lucide-react";

import { api, formatApiError, absoluteAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { displayCurrencyNumber, fromDisplayCurrency } from "@/utils/pricing";

const TTS_VOICES = [
    "alloy", "echo", "fable", "onyx", "nova", "shimmer", "sage", "coral", "ash",
];

export default function AISettings() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newRow, setNewRow] = useState({ source_token: "", spoken_form: "", context: "" });
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ source_token: "", spoken_form: "", context: "" });
    const [testOpen, setTestOpen] = useState(false);
    const [testInput, setTestInput] = useState({ text: "", voice_id: "onyx", speed: 1.0 });
    const [testOutput, setTestOutput] = useState(null);
    const [orgSettings, setOrgSettings] = useState(null);
    const [voiceBudget, setVoiceBudget] = useState(2.0);
    const [budgetSaving, setBudgetSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [{ data }, s] = await Promise.all([
                api.get("/org/pronunciations"),
                api.get("/org/settings"),
            ]);
            setRows(data);
            setOrgSettings(s.data);
            // Keep voiceBudget in RAW backend units; input uses
            // displayCurrencyNumber for value, fromDisplayCurrency on change.
            setVoiceBudget(Number(s.data?.voice_generation_daily_budget_usd ?? 2));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!newRow.source_token.trim() || !newRow.spoken_form.trim()) {
            toast.error("Both fields required");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/org/pronunciations", newRow);
            setRows((r) => [...r, data]);
            setNewRow({ source_token: "", spoken_form: "", context: "" });
            toast.success("Added");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const startEdit = (row) => {
        setEditingId(row.id);
        setEditForm({
            source_token: row.source_token,
            spoken_form: row.spoken_form,
            context: row.context || "",
        });
    };

    const saveEdit = async () => {
        if (!editingId) return;
        try {
            const { data } = await api.patch(`/org/pronunciations/${editingId}`, editForm);
            setRows((r) => r.map((x) => (x.id === editingId ? data : x)));
            setEditingId(null);
            toast.success("Saved");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!confirm("Remove this pronunciation?")) return;
        try {
            await api.delete(`/org/pronunciations/${id}`);
            setRows((r) => r.filter((x) => x.id !== id));
            toast.success("Removed");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const runTest = async (rowToPreview) => {
        const text = rowToPreview
            ? `The sample says: ${rowToPreview.source_token}.`
            : testInput.text;
        if (!text.trim()) { toast.error("Add sample text"); return; }
        try {
            const { data } = await api.post("/org/pronunciations/test", {
                text, voice_id: testInput.voice_id, speed: testInput.speed,
            });
            setTestOutput(data);
            if (rowToPreview) setTestOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveBudget = async () => {
        setBudgetSaving(true);
        try {
            // voiceBudget is held in RAW units; persist as-is.
            await api.put("/org/settings", {
                voice_generation_daily_budget_usd: Number(voiceBudget),
            });
            toast.success("Voice budget updated");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBudgetSaving(false); }
    };

    const defaults = useMemo(() => rows.filter((r) => r.is_default), [rows]);
    const custom = useMemo(() => rows.filter((r) => !r.is_default), [rows]);

    return (
        <div data-testid="ai-settings-page" className="space-y-10 pb-14">
            <header className="border-b border-neutral-200 pb-5">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">Admin</div>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-neutral-900">AI Settings</h1>
                <p className="mt-1 text-sm text-neutral-500 max-w-2xl">
                    Configure how the platform speaks technical terms in narration and cap daily spend
                    on AI voice generation. Changes take effect on the next scene voice generation.
                </p>
            </header>

            {/* Voice budget */}
            <section data-testid="voice-budget-section" className="space-y-3">
                <h2 className="text-sm font-semibold text-neutral-900">Voice generation budget</h2>
                <div className="flex items-end gap-3 max-w-md">
                    <div className="flex-1">
                        <Label htmlFor="voice-budget">Daily budget (USD)</Label>
                        <Input
                            id="voice-budget"
                            data-testid="voice-budget-input"
                            type="number"
                            min="0"
                            step="0.5"
                            value={displayCurrencyNumber(voiceBudget)}
                            onChange={(e) => setVoiceBudget(fromDisplayCurrency(e.target.value))}
                        />
                    </div>
                    <Button
                        data-testid="voice-budget-save"
                        onClick={saveBudget}
                        disabled={budgetSaving}
                    >
                        {budgetSaving ? "Saving…" : "Save budget"}
                    </Button>
                </div>
                <p className="text-xs text-neutral-500 max-w-xl">
                    Soft-warn at 1× • hard-block at 2× of the configured budget across all scenes in this organisation.
                </p>
            </section>

            {/* Pronunciations */}
            <section data-testid="pronunciations-section" className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <ShieldCheck size={16} strokeWidth={1.75} className="text-emerald-600" />
                            Pronunciation dictionary
                        </h2>
                        <p className="text-xs text-neutral-500 max-w-2xl mt-1">
                            Add rules to override how the trainer pronounces acronyms, standards, and
                            technical shorthand. Numeric values are never modified.
                        </p>
                    </div>
                    <Dialog open={testOpen} onOpenChange={setTestOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="open-test-tts" variant="secondary" onClick={() => { setTestOpen(true); setTestOutput(null); }}>
                                <PlayCircle size={15} strokeWidth={1.75} /> Test with TTS
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-xl">
                            <DialogHeader>
                                <DialogTitle>Test the pronunciation dictionary</DialogTitle>
                                <DialogDescription>
                                    Type a sentence and preview how the normalizer expands technical terms.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <Textarea
                                    data-testid="tts-test-text"
                                    value={testInput.text}
                                    onChange={(e) => setTestInput({ ...testInput, text: e.target.value })}
                                    maxLength={400}
                                    placeholder="e.g. Set the DSC to 30 °C and hold at 5 ml/min."
                                    rows={4}
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Voice</Label>
                                        <Select
                                            value={testInput.voice_id}
                                            onValueChange={(v) => setTestInput({ ...testInput, voice_id: v })}
                                        >
                                            <SelectTrigger data-testid="tts-test-voice"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {TTS_VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Speed</Label>
                                        <Input
                                            type="number" min="0.5" max="1.5" step="0.05"
                                            value={testInput.speed}
                                            onChange={(e) => setTestInput({ ...testInput, speed: parseFloat(e.target.value) })}
                                            data-testid="tts-test-speed"
                                        />
                                    </div>
                                </div>
                                {testOutput && (
                                    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 space-y-2">
                                        <div className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">Normalized</div>
                                        <div className="text-sm text-neutral-800" data-testid="tts-test-normalized">{testOutput.normalized_text}</div>
                                        <audio
                                            data-testid="tts-test-audio"
                                            controls
                                            src={absoluteAssetUrl(testOutput.signed_url)}
                                            className="w-full mt-2"
                                        />
                                    </div>
                                )}
                            </div>
                            <DialogFooter>
                                <Button data-testid="tts-test-run" onClick={() => runTest()}>Run test</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Add row */}
                <div className="rounded-lg border border-neutral-200 bg-white p-4">
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-3">
                            <Label>Source token</Label>
                            <Input
                                data-testid="pron-new-token"
                                placeholder="DSC"
                                value={newRow.source_token}
                                onChange={(e) => setNewRow({ ...newRow, source_token: e.target.value })}
                            />
                        </div>
                        <div className="col-span-5">
                            <Label>Spoken as</Label>
                            <Input
                                data-testid="pron-new-spoken"
                                placeholder="differential scanning calorimetry"
                                value={newRow.spoken_form}
                                onChange={(e) => setNewRow({ ...newRow, spoken_form: e.target.value })}
                            />
                        </div>
                        <div className="col-span-3">
                            <Label>Context</Label>
                            <Input
                                data-testid="pron-new-context"
                                placeholder="optional"
                                value={newRow.context}
                                onChange={(e) => setNewRow({ ...newRow, context: e.target.value })}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <Button data-testid="pron-add-btn" onClick={add} disabled={saving} className="w-full">
                                <Plus size={15} strokeWidth={1.75} />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Custom */}
                <RowsTable
                    title={`Custom rules (${custom.length})`}
                    rows={custom}
                    editingId={editingId}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    onEdit={startEdit}
                    onSave={saveEdit}
                    onCancel={() => setEditingId(null)}
                    onRemove={remove}
                    onTest={runTest}
                    testid="pron-custom-table"
                    loading={loading}
                    emptyText="No custom rules yet — add one above or leave it to the defaults."
                />

                {/* Defaults */}
                <RowsTable
                    title={`Built-in defaults (${defaults.length})`}
                    rows={defaults}
                    editingId={editingId}
                    editForm={editForm}
                    setEditForm={setEditForm}
                    onEdit={startEdit}
                    onSave={saveEdit}
                    onCancel={() => setEditingId(null)}
                    onRemove={remove}
                    onTest={runTest}
                    testid="pron-defaults-table"
                    isDefault
                    loading={loading}
                    emptyText="No built-in defaults."
                />
            </section>
        </div>
    );
}

function RowsTable({
    title, rows, editingId, editForm, setEditForm,
    onEdit, onSave, onCancel, onRemove, onTest,
    testid, isDefault, loading, emptyText,
}) {
    return (
        <div data-testid={testid} className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b border-neutral-200 text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center justify-between">
                <span>{title}</span>
                {isDefault && (
                    <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Auto-seeded</span>
                )}
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[180px]">Source token</TableHead>
                        <TableHead>Spoken as</TableHead>
                        <TableHead className="w-[180px]">Context</TableHead>
                        <TableHead className="w-[140px] text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading && (
                        <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-neutral-500">Loading…</TableCell></TableRow>
                    )}
                    {!loading && !rows.length && (
                        <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-neutral-500">{emptyText}</TableCell></TableRow>
                    )}
                    {rows.map((r) => (
                        <TableRow key={r.id} data-testid={`pron-row-${r.source_token}`}>
                            {editingId === r.id ? (
                                <>
                                    <TableCell><Input value={editForm.source_token} onChange={(e) => setEditForm({ ...editForm, source_token: e.target.value })} /></TableCell>
                                    <TableCell><Input value={editForm.spoken_form} onChange={(e) => setEditForm({ ...editForm, spoken_form: e.target.value })} /></TableCell>
                                    <TableCell><Input value={editForm.context} onChange={(e) => setEditForm({ ...editForm, context: e.target.value })} /></TableCell>
                                    <TableCell className="text-right space-x-1.5">
                                        <Button size="sm" onClick={onSave} data-testid={`pron-save-${r.source_token}`}>Save</Button>
                                        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
                                    </TableCell>
                                </>
                            ) : (
                                <>
                                    <TableCell className="font-mono text-sm text-neutral-900">{r.source_token}</TableCell>
                                    <TableCell className="text-sm text-neutral-700">{r.spoken_form}</TableCell>
                                    <TableCell className="text-xs text-neutral-500">{r.context || "—"}</TableCell>
                                    <TableCell className="text-right space-x-1.5">
                                        <Button size="sm" variant="ghost" onClick={() => onTest(r)} title="Preview" data-testid={`pron-test-${r.source_token}`}>
                                            <Volume2 size={14} strokeWidth={1.75} />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => onEdit(r)} title="Edit" data-testid={`pron-edit-${r.source_token}`}>
                                            <Pencil size={14} strokeWidth={1.75} />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => onRemove(r.id)} title="Remove" data-testid={`pron-remove-${r.source_token}`}>
                                            <Trash2 size={14} strokeWidth={1.75} className="text-red-600" />
                                        </Button>
                                    </TableCell>
                                </>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
